/**
 * ScheduleEditPopover: the small "move this" dialog opened from the pencil icon on a Schedule row. A month calendar to pick the new
 * day and, for timed items (one-time agent/workflow runs), a time. Tasks keep their time of day; only the date moves.
 */
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { T2 } from './AppShellV2.tsx';
import MiniCalendar from './MiniCalendar.tsx';

const EMPTY = new Map<string, number>();
const pad = (n: number) => String(n).padStart(2, '0');

export default function ScheduleEditPopover({ name, kindLabel, initial, withTime, onSave, onClose }: {
  name: string;
  kindLabel: string;
  initial: Date;
  withTime: boolean;
  onSave: (next: Date) => Promise<void> | void;
  onClose: () => void;
}) {
  const [day, setDay] = useState(new Date(initial));
  const [month, setMonth] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));
  const [time, setTime] = useState(`${pad(initial.getHours())}:${pad(initial.getMinutes())}`);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const save = async () => {
    const next = new Date(day);
    if (withTime) { const [h, m] = time.split(':').map(Number); next.setHours(h || 0, m || 0, 0, 0); }
    else next.setHours(initial.getHours(), initial.getMinutes(), initial.getSeconds(), 0);
    setSaving(true);
    try { await onSave(next); } finally { setSaving(false); }
  };

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={`Reschedule ${name}`} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(8,12,24,.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 320, maxWidth: '100%', background: T2.surface, border: `1px solid ${T2.border}`, borderRadius: 14, padding: 16, boxShadow: '0 24px 60px rgba(8,12,24,.35)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: T2.ink3 }}>Reschedule {kindLabel}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T2.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', cursor: 'pointer', color: T2.ink3, padding: 2 }}><X size={16} /></button>
        </div>
        <MiniCalendar month={month} onMonthChange={setMonth} selectedDate={day} onSelectDate={setDay} markedDates={EMPTY} />
        {withTime && (
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 12, fontSize: 12.5, color: T2.ink2 }}>
            Time
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required
              style={{ border: `1px solid ${T2.border}`, background: T2.surface, color: T2.ink, borderRadius: 8, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit' }} />
          </label>
        )}
        <div style={{ fontSize: 12, color: T2.ink3, marginTop: 10 }}>
          Moves to {day.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}{withTime ? ` at ${time}` : ''}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button type="button" onClick={onClose} style={{ border: `1px solid ${T2.border}`, background: T2.surface, color: T2.ink2, borderRadius: 9, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} style={{ border: 'none', background: T2.accent, color: 'var(--app-on-accent)', borderRadius: 9, padding: '7px 16px', fontSize: 12.5, fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
